import { Box, IconButton } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { ShippingTermsFieldGroup } from "@/components/Forms"
import { FormSelect } from "@/components/UiKit/FormControls"
import { useGetWarehouseOptions } from "@/hooks/useGetWarehouseOptions"
import type { CostPackageSchemaFormValues } from "@/screens/Pricing/schema/CostPackageSchema"

interface AddCostPackageFormProps {
  productId: number
}

export const AddCostPackageForm: FC<AddCostPackageFormProps> = ({ productId }) => {
  const { control } = useFormContext<CostPackageSchemaFormValues>()
  const { fields, append, remove } = useFieldArray<CostPackageSchemaFormValues, "warehouses">({
    control,
    name: "warehouses",
  })
  const { warehouseOptions } = useGetWarehouseOptions({ isActive: true, productId })

  const handleAdditionalWarehouse = () => {
    append({ shipToWarehouseAreaId: "" })
  }

  return (
    <Box className='flex flex-col gap-y-4'>
      <Box className='flex flex-col gap-y-2'>
        {fields.map((field, index) => (
          <Box key={field.id} className='flex items-end gap-x-2'>
            <FormSelect
              control={control}
              name={`warehouses.${index}.shipToWarehouseAreaId`}
              label={index === 0 ? "Warehouse" : undefined}
              required
              placeholder='Select...'
              options={warehouseOptions}
              className='flex-1'
              data-testid={`pricing.add-cost-package.warehouse-field-${index}`}
            />
            <IconButton
              size='small'
              className='mb-1'
              disabled={fields.length <= 1}
              onClick={() => remove(index)}
              aria-label='remove warehouse'
              data-testid={`pricing.add-cost-package.warehouse-remove-button-${index}`}
            >
              <DeleteOutlined fontSize='small' />
            </IconButton>
          </Box>
        ))}
        <Box className='flex justify-end'>
          <Button
            appearance='text'
            size='small'
            disabled={fields.length >= warehouseOptions.length}
            startIcon={<AddOutlined />}
            onClick={handleAdditionalWarehouse}
            data-testid='pricing.add-cost-package.additional-warehouse-button'
          >
            Additional Warehouse
          </Button>
        </Box>
      </Box>
      <ShippingTermsFieldGroup
        testIdPrefix='pricing.add-cost-package'
        fieldOrder={["incoterms", "incotermsLocationId", "freightTerms"]}
      />
    </Box>
  )
}
